import { formatCredits, formatCreditsWithUnit, formatDate } from '@lib/utils/units';
import { billingStore } from '@stores/Billing';
import { CreditUsageType, creditUsageStatsStore } from '@stores/CreditUsageStats';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react-lite';
import css from './Usage.module.scss';
import { UsageBar } from './UsageBar';
import { UsagePopout } from './UsagePopout';

const CREDIT_USAGE_LABEL: Record<CreditUsageType, string> = {
  chat: 'Agent chats',
  render: 'Renders',
  media: 'Media indexing',
  search: 'Search',
  storage: 'Storage',
  external: 'Sub-products',
};

/**
 * Credits spent per category since the usage window opened, then what the window's notes let
 * expire unused. Mounted only while the popover or drawer is open, and observing the stats is
 * what fetches them, so the request goes out when the user asks for it.
 */
const CreditsBreakdown = observer(({ since }: { since: number }) => {
  const stats = creditUsageStatsStore.currentStats;
  const expired = billingStore.usageWindowExpiredCredits;

  let body;
  if (stats) {
    const rows = stats.filter((stat) => stat.credits > 0).sort((a, b) => b.credits - a.credits);
    const total = rows.reduce((sum, row) => sum + row.credits, 0);
    body =
      rows.length === 0 ? (
        <span className={css.breakdownStatus}>Nothing spent yet.</span>
      ) : (
        rows.map((row) => (
          <div key={row.type} className={css.breakdownRow}>
            <div className={css.breakdownRowLabel}>
              <span>{CREDIT_USAGE_LABEL[row.type]}</span>
              <span className={css.breakdownRowCredits}>{formatCredits(row.credits)}</span>
            </div>
            <UsageBar
              height={4}
              segments={[
                {
                  label: CREDIT_USAGE_LABEL[row.type],
                  color: 'var(--mui-palette-text-inactive)',
                  value: row.credits,
                  tooltipText: formatCreditsWithUnit(row.credits),
                },
                {
                  label: 'Other categories',
                  color: 'transparent',
                  value: total - row.credits,
                  tooltipText: `${formatCreditsWithUnit(total - row.credits)} on other categories`,
                },
              ]}
            />
          </div>
        ))
      );
  } else if (creditUsageStatsStore.error) {
    body = <span className={css.breakdownStatus}>Couldn&apos;t load the breakdown.</span>;
  } else {
    body = <span className={css.breakdownStatus}>Loading…</span>;
  }

  return (
    <div className={css.breakdown}>
      <span className={css.breakdownTitle}>Spent since {formatDate(since)}</span>
      {body}
      {expired > 0 && (
        <div className={css.breakdownExpired}>
          <span>Expired before use</span>
          <span className={css.breakdownRowCredits}>{formatCredits(expired)}</span>
        </div>
      )}
    </div>
  );
});

/** "Breakdown", opening `CreditsBreakdown`. */
export const CreditsBreakdownButton = observer(() => {
  const since = billingStore.usageWindowStart;
  if (since === null) return null;
  return (
    <UsagePopout
      label="Breakdown"
      triggerClassName={css.breakdownButton}
      onOpen={() => trackingStore.creditsBreakdownOpened({ panel: 'spend' })}
    >
      <CreditsBreakdown since={since} />
    </UsagePopout>
  );
});
